import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Search, RefreshCw, Receipt, Clock, CreditCard, User, UtensilsCrossed,
  ChefHat, Check, Bell, FileText, X, Eye, Printer, MessageCircle, 
  MoreVertical, Phone, Bed, ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';

// Order status config
const ORDER_STATUS = {
  pending: { label: 'Pending', color: 'bg-red-500 text-white', icon: Bell },
  preparing: { label: 'Preparing', color: 'bg-amber-500 text-white', icon: ChefHat },
  ready: { label: 'Ready', color: 'bg-green-500 text-white', icon: Check },
  served: { label: 'Served', color: 'bg-blue-500 text-white', icon: UtensilsCrossed },
  completed: { label: 'Completed', color: 'bg-emerald-600 text-white', icon: Check },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500 text-white', icon: X },
};

// Order type config
const ORDER_TYPES = {
  dine_in: { label: 'Dine In', icon: UtensilsCrossed },
  takeaway: { label: 'Takeaway', icon: ShoppingBag },
  room_service: { label: 'Room Service', icon: Bed }
};

const OrderListDialog = ({ 
  open, 
  onOpenChange, 
  restaurantId,
  onViewOrder,
  onOrderStatusChange
}) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  
  // Invoice dialog
  const [invoiceActionsOpen, setInvoiceActionsOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  
  // Order Details dialog
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);

  useEffect(() => {
    if (open) {
      loadOrders();
    }
  }, [open, statusFilter, orderTypeFilter]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (restaurantId) params.append('restaurant_id', restaurantId);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (orderTypeFilter !== 'all') params.append('order_type', orderTypeFilter);
      
      const res = await api.get(`/orders?${params.toString()}`);
      let filteredOrders = res.data;
      
      // Sort by date descending (newest first)
      filteredOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setOrders(filteredOrders);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Filter orders by search
  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.id?.toLowerCase().includes(query) ||
      order.customer_name?.toLowerCase().includes(query) ||
      order.table_number?.toString().includes(query) ||
      order.room_number?.toString().includes(query) ||
      order.customer_phone?.includes(query)
    );
  });

  // Generate bill/invoice
  const generateBill = async (order) => {
    try {
      const invoiceData = {
        order_ids: [order.id],
        customer_name: order.customer_name || 'Guest',
        notes: order.notes || ''
      };

      const res = await api.post('/invoices/from-orders', invoiceData);
      
      // Update order status to completed
      await api.patch(`/orders/${order.id}/status?status=completed`);
      
      toast.success('Invoice generated!');
      
      // Show invoice actions
      setCurrentInvoice(res.data);
      setWhatsappNumber(order.customer_phone || '');
      setInvoiceActionsOpen(true);
      
      // Refresh orders
      loadOrders();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to generate invoice');
    }
  };

  // Print invoice
  const printInvoice = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      toast.success('Opening print dialog...');
    } catch (error) {
      toast.error('Failed to print invoice');
    }
  };

  // View invoice PDF
  const viewInvoice = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to view invoice');
    }
  };

  // Send WhatsApp
  const sendWhatsApp = async () => {
    if (!currentInvoice?.id || !whatsappNumber) {
      toast.error('Please enter WhatsApp number');
      return;
    }
    
    setSendingWhatsApp(true);
    try {
      await api.post(`/invoices/${currentInvoice.id}/send-whatsapp`, {
        phone_number: whatsappNumber
      });
      toast.success('Invoice sent via WhatsApp!');
      setInvoiceActionsOpen(false);
    } catch (error) {
      toast.error('Failed to send WhatsApp. Please configure WhatsApp settings.');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Update order status in local state (no full reload)
  const updateOrderInState = (orderId, newStatus) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus, updated_at: new Date().toISOString() }
          : order
      )
    );
  };

  // Mark order as completed
  const markCompleted = async (orderId) => {
    try {
      await api.patch(`/orders/${orderId}/status?status=completed`);
      updateOrderInState(orderId, 'completed'); // Update locally without full reload
      toast.success('Order marked as completed');
      onOrderStatusChange?.(); // Refresh POS table orders
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  // Cancel order
  const cancelOrder = async (orderId) => {
    try {
      await api.patch(`/orders/${orderId}/status?status=cancelled`);
      updateOrderInState(orderId, 'cancelled'); // Update locally without full reload
      toast.success('Order cancelled');
      onOrderStatusChange?.(); // Refresh POS table orders
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const getOrderTypeIcon = (type) => {
    const Icon = ORDER_TYPES[type]?.icon || UtensilsCrossed;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Order History
              <Badge variant="outline">{filteredOrders.length} orders</Badge>
            </DialogTitle>
          </DialogHeader>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 pb-3 border-b">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by order ID, customer, table..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="preparing">Preparing</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="served">Served</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={orderTypeFilter} onValueChange={setOrderTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Order Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="dine_in">Dine In</SelectItem>
                <SelectItem value="takeaway">Takeaway</SelectItem>
                <SelectItem value="room_service">Room Service</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" size="sm" onClick={loadOrders}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Orders List - Fixed height with scroll */}
          <div className="flex-1 min-h-0 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No orders found</p>
              </div>
            ) : (
              <div className="space-y-2 py-2 pr-2">
                {filteredOrders.map((order) => {
                  const statusInfo = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
                  const StatusIcon = statusInfo.icon;
                  const hasInvoice = order.invoice_id;
                  
                  return (
                    <div
                      key={order.id}
                      className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      {/* Order Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold text-sm">
                            #{order.id?.slice(-6).toUpperCase()}
                          </span>
                          <Badge className={statusInfo.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                          <Badge variant="outline" className="gap-1">
                            {getOrderTypeIcon(order.order_type)}
                            {ORDER_TYPES[order.order_type]?.label}
                          </Badge>
                          {order.payment_status === 'paid' && (
                            <Badge className="bg-green-100 text-green-700">
                              <CreditCard className="h-3 w-3 mr-1" />
                              Paid
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(order.created_at).toLocaleString()}
                          </span>
                          {order.table_number && (
                            <span className="flex items-center gap-1">
                              <UtensilsCrossed className="h-3 w-3" />
                              Table {order.table_number}
                            </span>
                          )}
                          {order.room_number && (
                            <span className="flex items-center gap-1">
                              <Bed className="h-3 w-3" />
                              Room {order.room_number}
                            </span>
                          )}
                          {order.customer_name && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {order.customer_name}
                            </span>
                          )}
                          {order.customer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {order.customer_phone}
                            </span>
                          )}
                        </div>
                        
                        {/* Items preview */}
                        <div className="text-xs text-muted-foreground mt-1">
                          {order.items?.slice(0, 3).map((item, i) => (
                            <span key={i}>
                              {i > 0 && ', '}
                              {item.quantity}x {item.item_name}
                            </span>
                          ))}
                          {order.items?.length > 3 && (
                            <span className="text-muted-foreground"> +{order.items.length - 3} more</span>
                          )}
                        </div>
                      </div>

                      {/* Total */}
                      <div className="text-right">
                        <div className="font-bold text-lg">₹{(order.total_amount || 0).toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">
                          {order.items?.reduce((sum, i) => sum + i.quantity, 0)} items
                        </div>
                      </div>

                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => {
                            setSelectedOrder(order);
                            setOrderDetailsOpen(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          
                          <DropdownMenuSeparator />
                          
                          {/* Bill/Invoice Actions */}
                          {hasInvoice ? (
                            <>
                              <DropdownMenuItem onClick={() => viewInvoice(order.invoice_id)}>
                                <FileText className="h-4 w-4 mr-2" />
                                View Invoice
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => printInvoice(order.invoice_id)}>
                                <Printer className="h-4 w-4 mr-2" />
                                Print Invoice
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setCurrentInvoice({ id: order.invoice_id });
                                setWhatsappNumber(order.customer_phone || '');
                                setInvoiceActionsOpen(true);
                              }}>
                                <MessageCircle className="h-4 w-4 mr-2" />
                                Send WhatsApp
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => generateBill(order)}
                              disabled={order.status === 'cancelled'}
                            >
                              <Receipt className="h-4 w-4 mr-2" />
                              Generate Bill
                            </DropdownMenuItem>
                          )}
                          
                          <DropdownMenuSeparator />
                          
                          {/* Status Actions */}
                          {order.status !== 'completed' && order.status !== 'cancelled' && (
                            <DropdownMenuItem onClick={() => markCompleted(order.id)}>
                              <Check className="h-4 w-4 mr-2" />
                              Mark Completed
                            </DropdownMenuItem>
                          )}
                          
                          {order.status !== 'cancelled' && order.status !== 'completed' && (
                            <DropdownMenuItem 
                              onClick={() => cancelOrder(order.id)}
                              className="text-red-600"
                            >
                              <X className="h-4 w-4 mr-2" />
                              Cancel Order
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Send Dialog */}
      <Dialog open={invoiceActionsOpen} onOpenChange={setInvoiceActionsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-600" />
              Send Invoice via WhatsApp
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">WhatsApp Number</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter phone number with country code"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Include country code (e.g., +91 for India)
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setInvoiceActionsOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={sendWhatsApp}
                disabled={sendingWhatsApp || !whatsappNumber}
              >
                {sendingWhatsApp ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <MessageCircle className="h-4 w-4 mr-2" />
                )}
                Send
              </Button>
            </div>
            
            {/* Quick Actions */}
            <div className="flex gap-2 pt-2 border-t">
              <Button 
                variant="outline" 
                size="sm"
                className="flex-1"
                onClick={() => currentInvoice?.id && viewInvoice(currentInvoice.id)}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="flex-1"
                onClick={() => currentInvoice?.id && printInvoice(currentInvoice.id)}
              >
                <Printer className="h-4 w-4 mr-1" />
                Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={orderDetailsOpen} onOpenChange={setOrderDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Order Details #{selectedOrder?.id?.slice(-6).toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <Badge className={`ml-2 ${ORDER_STATUS[selectedOrder.status]?.color || 'bg-gray-500'}`}>
                    {ORDER_STATUS[selectedOrder.status]?.label || selectedOrder.status}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Type:</span>
                  <span className="ml-2 font-medium">{ORDER_TYPES[selectedOrder.order_type]?.label || selectedOrder.order_type}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Date:</span>
                  <span className="ml-2">{new Date(selectedOrder.created_at).toLocaleString()}</span>
                </div>
                {selectedOrder.table_number && (
                  <div>
                    <span className="text-muted-foreground">Table:</span>
                    <span className="ml-2 font-medium">{selectedOrder.table_number}</span>
                  </div>
                )}
                {selectedOrder.room_number && (
                  <div>
                    <span className="text-muted-foreground">Room:</span>
                    <span className="ml-2 font-medium">{selectedOrder.room_number}</span>
                  </div>
                )}
                {selectedOrder.customer_name && (
                  <div>
                    <span className="text-muted-foreground">Customer:</span>
                    <span className="ml-2 font-medium">{selectedOrder.customer_name}</span>
                  </div>
                )}
                {selectedOrder.customer_phone && (
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <span className="ml-2">{selectedOrder.customer_phone}</span>
                  </div>
                )}
              </div>

              {/* Items */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted px-3 py-2 font-semibold text-sm flex justify-between">
                  <span>Items</span>
                  <span>Amount</span>
                </div>
                <div className="divide-y">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="px-3 py-2 flex justify-between items-center">
                      <div>
                        <span className="font-medium">{item.quantity}x {item.item_name}</span>
                        {item.status && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            {item.status}
                          </Badge>
                        )}
                      </div>
                      <span className="font-medium">₹{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₹{(selectedOrder.subtotal || 0).toFixed(2)}</span>
                </div>
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-₹{selectedOrder.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax</span>
                  <span>₹{(selectedOrder.tax || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>₹{(selectedOrder.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-muted rounded-lg p-3">
                  <span className="text-sm text-muted-foreground">Notes:</span>
                  <p className="text-sm mt-1">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrderListDialog;
